import React, { useEffect, useImperativeHandle, useRef } from "react";
import { usePlaidLink, PlaidLinkOnEventMetadata } from "react-plaid-link";

type EventHandler = (
  eventName: string,
  metadata: PlaidLinkOnEventMetadata | unknown
) => void;

export interface PlaidUpdateLauncherHandle {
  open: () => void;
  ready: boolean;
}

interface Props {
  token: string;
  itemId: string;
  onSuccess: () => void;
  onExit: (err: unknown, metadata: unknown) => void;
  onEvent?: EventHandler;
  env: "production" | "sandbox" | "development";
}

/**
 * Mounts a usePlaidLink instance bound to a single update-mode token and
 * exposes its `open()` via a forwarded ref so the parent can call it
 * synchronously from the user-click handler. Plaid Link silently no-op's
 * when open() is called outside the user-gesture window (the modal needs
 * to be triggered by a click, not a useEffect), so the parent should
 * pre-mount this component once the token is fetched and only invoke
 * open() inside the click handler.
 */
const PlaidUpdateLauncher = React.forwardRef<PlaidUpdateLauncherHandle, Props>(
  ({ token, itemId, onSuccess, onExit, onEvent, env }, ref) => {
    // Latch callbacks so usePlaidLink's config doesn't change identity on
    // every parent render.
    const onSuccessRef = useRef(onSuccess);
    const onExitRef = useRef(onExit);
    const onEventRef = useRef(onEvent);
    useEffect(() => {
      onSuccessRef.current = onSuccess;
    }, [onSuccess]);
    useEffect(() => {
      onExitRef.current = onExit;
    }, [onExit]);
    useEffect(() => {
      onEventRef.current = onEvent;
    }, [onEvent]);

    const { open, ready, error } = usePlaidLink({
      token,
      env,
      onSuccess: () => onSuccessRef.current(),
      onExit: (err, metadata) => onExitRef.current(err, metadata),
      onEvent: (eventName, metadata) =>
        onEventRef.current?.(eventName, metadata),
    });

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          console.log("[PlaidUpdateLauncher] open()", {
            itemId,
            ready,
            token: token.slice(0, 28) + "...",
          });
          open();
        },
        ready,
      }),
      [open, ready, itemId, token]
    );

    useEffect(() => {
      if (error) {
        console.error("[PlaidUpdateLauncher] usePlaidLink error", error);
      }
    }, [error]);

    return null;
  }
);

PlaidUpdateLauncher.displayName = "PlaidUpdateLauncher";

export default PlaidUpdateLauncher;
